<script setup lang="ts">
// @ts-nocheck
import { Props, setup } from '@/ai/components/targetList.setup';
import BaseGrid from '@/common/components/molecules/grid/BaseGrid.vue';
import { listViewGridOption } from '@/common/define/grid.define';
import { alertLevelSortFn } from '@/alert/utils/utils';

const gridOption = {
  ...listViewGridOption,
  customAscFunction: { targetAlertLevel: alertLevelSortFn },
};

const props = defineProps<Props>();
const {
  t,
  columns,
  toggleValue,
  convertedRows,
  mappedTargetData,
  onChange,
  onDetail,
  getIconType,
} = setup(props);
</script>

<template>
  <transition name="fade">
    <div
      v-if="true"
      class="target-list__wrapper"
    >
      <div class="target-list__header">
        <ev-button-group class="target-button--group">
          <ev-button
            class="target-button"
            :class="{ selected: toggleValue === 'list' }"
            :type="toggleValue === 'list' ? 'primary' : 'default'"
            @click="() => onChange('list')"
          >
            <ev-icon icon="icon-list" />
            <p class="list-text">{{ t('WORD.LIST') }}</p>
          </ev-button>
          <ev-button
            class="target-button"
            :class="{ selected: toggleValue === 'icon' }"
            :type="toggleValue === 'icon' ? 'primary' : 'default'"
            @click="() => onChange('icon')"
          >
            <ev-icon icon="icon-icon" />
            <p class="list-text">{{ t('WORD.ICON') }}</p>
          </ev-button>
        </ev-button-group>
      </div>
      <div class="target-list__content">
        <template v-if="toggleValue === 'list'">
          <base-grid
            :columns="columns"
            :rows="convertedRows"
            :option="gridOption"
            class="list__wrapper"
          >
            <template #targetKind="{ item }">
              <div
                :key="item"
                class="list--text"
              >
                <ev-icon :icon="getIconType(item.row[2][0])" />
                {{ item.row[2][item.column.index] }}
              </div>
            </template>
            <template #targetName="{ item }">
              <div
                :key="item"
                class="list--text link-target"
                @click="() => onDetail(item.row[2])"
              >
                {{ item.row[2][item.column.index] }}
              </div>
            </template>
          </base-grid>
        </template>
        <template v-else-if="toggleValue === 'icon'">
          <transition-group
            name="fade"
            tag="div"
          >
            <div
              v-for="(targets, category) in mappedTargetData"
              :key="category"
            >
              <div class="icon__header">{{ category }} ({{ targets.length }})</div>
              <div class="icon__wrapper">
                <div
                  v-for="(target, index) in targets"
                  :key="index"
                  class="card-box link-target"
                  @click="() => onDetail(target)"
                >
                  <div :class="['target-box', target.targetAlertLevel.toLocaleLowerCase()]">
                    <ev-icon
                      :icon="getIconType(target.targetKind)"
                      class="target-icon"
                    />
                  </div>
                  <span :title="target.targetName">{{ target.targetName }}</span>
                </div>
              </div>
            </div>
          </transition-group>
        </template>
      </div>
    </div>
  </transition>
</template>

<style scoped lang="scss">
.target-list {
  &__wrapper {
    display: flex;
    flex-direction: column;
    width: 75%;
    padding: 16px;
    margin-bottom: 12px;
    border-radius: 8px;
    background-color: var(--color-gray-07-00);
    box-shadow: 0 2px 4px rgb(0, 0, 0, 0.1);

    .target-box {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 2px;
    }

    .target-icon {
      width: 22px;
      height: 22px;
      background-color: var(--color-gray-00-00);
    }

    .normal {
      border: 1px solid var(--color-green-03-07);
      background-color: var(--color-green-06-04);
    }

    .warning {
      border: 1px solid var(--color-yellow-03-07);
      background-color: var(--color-yellow-06-04);
    }

    .critical {
      border: 1px solid var(--color-red-03-07);
      background-color: var(--color-red-06-04);
    }
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;

    .target-button--group {
      display: flex;
    }
    .target-button {
      display: flex;
      align-items: center;
      width: 53px !important;
      height: 20px !important;
      padding: 5px 2px 6px 0 !important;
      cursor: pointer;
      transition: background-color 0.3s ease;

      p {
        font-size: 11px;
        font-weight: 400;
      }
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;

    :deep(.list__wrapper.base-grid .base-grid__content .grid-wrapper) {
      height: 500px !important;
    }

    .list {
      &--text {
        display: flex;
        gap: 8px;
        align-items: center;
        font-size: 14px;
        color: var(--main-font-color);

        &.link-target:hover {
          cursor: pointer;
          text-decoration: underline;
        }
      }
    }

    .icon {
      &__header {
        align-items: center;
        padding: 12px 12px 8px;
        border: 1px solid var(--color-gray-05-05);
        background-color: var(--color-gray-09-05);
        font-size: 12px;
        font-style: normal;
        font-weight: 700;
        line-height: 12px;
      }

      &__wrapper {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(78px, 1fr));
        gap: 8px;
        padding: 12px;
        border: 1px solid var(--color-gray-05-05);
        transition: background-color 0.3s ease;

        .card-box {
          display: flex;
          flex-direction: column;
          gap: 8px;
          align-items: center;
          justify-content: center;
          width: 81px;
          height: 92px;
          padding: 0 4px;
          border: 1px solid var(--color-gray-05-05);
          border-radius: 2px;
          background-color: var(--color-gray-07-00);

          &:hover {
            border: 1px solid var(--color-blue-05-05);
          }
          &:active {
            background-color: var(--color-blue-09-01);
          }

          &.link-target:hover {
            cursor: pointer;
          }
        }

        span {
          width: 100%;
          overflow: hidden;
          font-size: 12px;
          font-weight: 400;
          color: var(--ai-target-list-text-color);
          text-align: center;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
    .target-icon-group {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(79px, 1fr));
      gap: 8px;
      padding: 12px;
      border: 1px solid var(--color-gray-05-05);
    }
  }
}
</style>
